import { css } from 'css-literal-loader/styled';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import MaskedInput from 'react-maskedinput';
import Layout from '@4c/layout';

import addressSchema from '../schema/address';
import stateData from '../utils/stateData';
import DropdownList from './DropdownList';
import Form from './Form';
import FormControl from './FormControl';
import Responsive from './Responsive';
import ZipInput from './ZipInput';

const messages = defineMessages({
  attention: {
    id: 'address.attention',
    defaultMessage: 'Attention',
  },
  name: {
    id: 'address.name',
    defaultMessage: 'Full Name',
  },
  line1: {
    id: 'address.line1',
    defaultMessage: 'Address Line',
  },
  line2: {
    id: 'address.line2',
    defaultMessage: 'Address Line 2',
  },
  city: {
    id: 'address.city',
    defaultMessage: 'City',
  },
  state: {
    id: 'address.state',
    defaultMessage: 'State',
  },
  phone: {
    id: 'address.phone',
    defaultMessage: 'Telephone',
  },
  email: {
    id: 'address.email',
    defaultMessage: 'Email',
  },
  postalCode: {
    id: 'address.postalCode',
    defaultMessage: 'Zip',
  },
  arrangedEventPickup: {
    id: 'address.arrangeEventPickup',
    defaultMessage: 'I have arranged to pick up my order at a Butterfly event',
  },
});

const styles = require('./AddressForm-styles.module.scss');

const DropdownFieldGroup = props => (
  <Form.FieldGroup {...props} as={DropdownList} />
);

const propTypes = {};

// @reach/Router checks autoFocus
const autoFocus = ref => {
  setTimeout(() => ref?.focus());
};

function AddressForm({
  children,
  name,
  email,
  isShipping,
  withEventPickup,
  ...props
}) {
  const prefix = n => (name ? `${name}.${n}` : n);

  return (
    <Responsive smDown>
      {isSmall => (
        <Layout
          {...props}
          pad={4}
          direction="column"
          as={name ? 'div' : Form}
          schema={!name ? addressSchema : undefined}
          focusFirstInvalidOnSubmit={!name ? true : undefined}
        >
          <Layout.Block
            grow
            label={messages.name}
            name={prefix('name')}
            as={Form.FieldGroup}
            inputRef={autoFocus}
          />
          {isShipping && (
            <Layout.Block
              grow
              label={messages.attention}
              name={prefix('attention')}
              as={Form.FieldGroup}
            />
          )}
          <Layout.Block
            grow
            name={prefix('line1')}
            as={Form.FieldGroup}
            label={messages.line1}
          />
          <Layout.Block
            grow
            name={prefix('line2')}
            as={Form.FieldGroup}
            label={messages.line2}
          />
          <Layout pad={4} direction={isSmall ? 'column' : 'row'}>
            <Layout.Block
              grow
              name={prefix('city')}
              as={Form.FieldGroup}
              label={messages.city}
            />
            <Layout.Block
              name={prefix('state')}
              className={styles.state}
              as={DropdownFieldGroup}
              data={stateData}
              label={messages.state}
            />
            <Layout.Block grow className={styles.zip}>
              <Form.FieldGroup
                as={ZipInput}
                name={prefix('postalCode')}
                label={messages.postalCode}
              />
            </Layout.Block>
          </Layout>
          <Layout pad={4} direction={isSmall ? 'column' : 'row'}>
            {!isSmall && (
              <Layout.Block
                grow
                noValidate
                name="email"
                as={Form.FieldGroup}
                label={messages.email}
              >
                {() => <FormControl name="email" disabled value={email} />}
              </Layout.Block>
            )}
            <Layout.Block grow>
              <Form.FieldGroup
                type="tel"
                formControlAs={MaskedInput}
                name={prefix('phone')}
                mask="(111) 111-1111"
                placeholder=" "
                label={messages.phone}
              />
            </Layout.Block>
          </Layout>
          {withEventPickup && (
            <Form.FieldGroup
              withTooltip
              tooltipTheme="danger"
              name={prefix('eventPickup')}
            >
              <FormattedMessage {...messages.arrangedEventPickup} />
            </Form.FieldGroup>
          )}
          {children}
        </Layout>
      )}
    </Responsive>
  );
}

AddressForm.propTypes = propTypes;

export default AddressForm;
